import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, PlusIcon, CogIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import BookingLinkGenerator from '../components/appointments/BookingLinkGenerator';
import AppointmentList from '../components/appointments/AppointmentList';
import { BookingSettings, Appointment } from '../types/appointments';
import { appointmentService as AppointmentService } from '../services/appointmentService';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/common/LoadingScreen';
import { Button } from '../components/common/Button';
import Modal from '../components/common/Modal';
import BookingSettingsForm from '../components/appointments/BookingSettingsForm';

const AppointmentBooking: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'appointments' | 'settings'>('appointments');
  const [settings, setSettings] = useState<BookingSettings[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingSettings, setEditingSettings] = useState<BookingSettings | null>(null);

  // Beállítások betöltése
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.organizationId) return;

      try {
        const bookingSettings = await AppointmentService.getBookingSettings(user.organizationId);
        setSettings(bookingSettings);
      } catch (error) {
        console.error('Error loading booking settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.organizationId]);

  // Foglalások betöltése
  useEffect(() => {
    const loadAppointments = async () => {
      if (!user?.organizationId || activeTab !== 'appointments') return;

      setAppointmentsLoading(true);
      try {
        const appointmentList = await AppointmentService.getAppointments(user.organizationId);
        setAppointments(appointmentList);
      } catch (error) {
        console.error('Error loading appointments:', error);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    loadAppointments();
  }, [user?.organizationId, activeTab]);

  const handleSettingsChange = () => {
    // Újratöltjük a beállításokat
    if (user?.organizationId) {
      AppointmentService.getBookingSettings(user.organizationId)
        .then(setSettings)
        .catch(console.error);
    }
  };

  const handleCreateSettings = () => {
    setEditingSettings(null);
    setShowSettingsModal(true);
  };

  const handleEditSettings = (setting: BookingSettings) => {
    setEditingSettings(setting);
    setShowSettingsModal(true);
  };

  const handleUpdateAppointmentStatus = async (appointmentId: string, status: Appointment['status']) => {
    try {
      await AppointmentService.updateAppointmentStatus(appointmentId, status);
      // Frissítjük a helyi listát
      setAppointments(prev => 
        prev.map(apt => 
          apt.id === appointmentId ? { ...apt, status } : apt
        )
      );
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    try {
      // TODO: Implement delete appointment in service
      console.log('Delete appointment:', appointmentId);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  const handleUpdateAppointment = async (updatedAppointment: Appointment) => {
    try {
      await AppointmentService.updateAppointment(
        updatedAppointment.id!, 
        updatedAppointment, 
        user?.email || undefined
      );
      setAppointments(prev => 
        prev.map(apt => apt.id === updatedAppointment.id ? updatedAppointment : apt)
      );
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      {/* Fejléc */}
      <CommonPageHeader
        title="Időpontfoglalás"
        subtitle="Kezeld az időpontjaidat és oszd meg a foglalási linkjeidet"
        icon={CalendarDaysIcon}
      >
        <Button
          variant="primary"
          onClick={handleCreateSettings}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>Új Beállítás</span>
        </Button>
      </CommonPageHeader>

      {/* Tab navigáció */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('appointments')}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors
                ${activeTab === 'appointments'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Foglalások</span>
              {appointments.length > 0 && (
                <span className={`
                  inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${activeTab === 'appointments' 
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                  }
                `}>
                  {appointments.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors
                ${activeTab === 'settings'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <CogIcon className="h-4 w-4" />
              <span>Beállítások & Linkek</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tartalom */}
      {activeTab === 'appointments' ? (
        /* Foglalások lista - teljes szélesség */
        <div className="px-4 sm:px-6 lg:px-8">
          <AppointmentList
            appointments={appointments}
            settings={settings}
            onUpdateStatus={handleUpdateAppointmentStatus}
            onDeleteAppointment={handleDeleteAppointment}
            onUpdateAppointment={handleUpdateAppointment}
            loading={appointmentsLoading}
          />
        </div>
      ) : (
        /* Beállítások - korlátozott szélesség */
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Bal oldal - Statisztikák */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Gyors Áttekintés
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Aktív beállítások:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {settings.filter(s => s.isActive).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Összes beállítás:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {settings.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Mai foglalások:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {appointments.filter(apt => {
                        const today = new Date();
                        const aptDate = apt.appointmentDate;
                        return aptDate.toDateString() === today.toDateString();
                      }).length}
                    </span>
                  </div>
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="outline"
                      onClick={handleCreateSettings}
                      className="w-full flex items-center justify-center space-x-2"
                    >
                      <CogIcon className="h-4 w-4" />
                      <span>Új Beállítás</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Jobb oldal - Link generátor */}
            <div className="lg:col-span-2">
              <BookingLinkGenerator
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Beállítások modal */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title=""
        size="5xl"
      >
        <BookingSettingsForm
          settings={editingSettings}
          onSave={(savedSettings) => {
            handleSettingsChange();
            setShowSettingsModal(false);
          }}
          onCancel={() => setShowSettingsModal(false)}
        />
      </Modal>
    </div>
  );
};


export default AppointmentBooking;
